import { useEffect, useMemo } from 'react';
import { navigate, switchTab } from '../../app/navigation';
import { areaCompletionFor, isInspectionWorkable, type V2Inspection } from '../../domain/inspection';
import { nyReportDue } from '../../domain/reportDue';
import { useT, type MessageKey } from '../../i18n/i18n';
import { aiJobsStore, jobCountsOf } from '../../state/aiJobsStore';
import { billingStore, ensureBillingStarted } from '../../state/billingStore';
import { useStore } from '../../state/createStore';
import { findingStore } from '../../state/findingStore';
import { areasOf, inspectionProgress, inspectionStore } from '../../state/inspectionStore';
import { inspectionIssueCount } from '../../state/issueCounts';
import { useInspectorProfile } from '../../state/preferences';
import { Banner, Icon } from '../../ui/components';
import { isWebVersion, requestPersistentWebStorage, webStorageStore } from '../../state/webStorage';
import type { IconName } from '../../ui/icons';
import { formatClock, formatDate } from '../../ui/hooks';
import { useShell } from '../../ui/shell';
import { clientOf, initialsOf, inspectionStageOf, localityOf, StageBadge, streetOf, type InspectionStage } from '../inspections/inspectionSummary';
import { OtherDevicesBanner } from '../inspections/OtherDevicesBanner';

// Home — the landing screen after sign-in (Owner 2026-09-11 on TestFlight 2.0.0 (84): 「这个就是
// 一个验屋页。要有一个像样的首页。」). A field inspector's day at a glance, in the Figma visual
// language, from real on-device data only:
//   greeting · date · licence   →  continue the open inspection (or start one)
//   quick actions               →  today + coming up (scheduled date / time)
//   to-dos                      →  NY five-business-day report reminders (OD-12, remind only),
//                                  ready for closeout, missing licence
//   this month                  →  inspections · reports generated · findings
// No analytics, no sync claims; the records live on this device.
const UPCOMING_DAYS = 14;
const UPCOMING_LIMIT = 3;

function dayStart(ts: number, addDays = 0): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + addDays);
  return d.getTime();
}

function monthRange(ts: number): [number, number] {
  const start = new Date(ts);
  start.setHours(0, 0, 0, 0);
  start.setDate(1);
  const end = new Date(start);
  end.setMonth(end.getMonth() + 1);
  return [start.getTime(), end.getTime()];
}

type DayPart = 'morning' | 'noon' | 'afternoon' | 'evening';

function dayPartOf(ts: number): DayPart {
  const hour = new Date(ts).getHours();
  if (hour >= 5 && hour < 11) return 'morning';
  if (hour >= 11 && hour < 13) return 'noon';
  if (hour >= 13 && hour < 18) return 'afternoon';
  return 'evening';
}

const GREETING_KEYS: Record<DayPart, { plain: MessageKey; named: MessageKey }> = {
  morning: { plain: 'home.greeting.morning', named: 'home.greetingNamed.morning' },
  noon: { plain: 'home.greeting.noon', named: 'home.greetingNamed.noon' },
  afternoon: { plain: 'home.greeting.afternoon', named: 'home.greetingNamed.afternoon' },
  evening: { plain: 'home.greeting.evening', named: 'home.greetingNamed.evening' }
};

type TodoTone = 'due' | 'overdue' | 'ready' | 'profile';

interface Todo {
  key: string;
  tone: TodoTone;
  title: string;
  body: string;
  open: () => void;
  testId: string;
}

export function HomeScreen() {
  const { t, tp, locale } = useT();
  const shell = useShell();
  const state = useStore(inspectionStore, (s) => s);
  const findingState = useStore(findingStore, (s) => s);
  const billing = useStore(billingStore, (s) => s);
  // Advanced AI jobs on this device (2026-09-13 §12): one row, only while there are any.
  const aiJobs = useStore(aiJobsStore, (s) => s.jobs);
  const aiCounts = useMemo(() => jobCountsOf({ jobs: aiJobs }, null), [aiJobs]);
  // The entitlement is read once the app is on its feet, so New Inspection already knows whether
  // this account may create a formal inspection (Owner D-5) and the row below can state 约剩 N 次.
  useEffect(() => {
    void ensureBillingStarted();
    void requestPersistentWebStorage();
  }, []);
  // Web version (item 45): the data lives only in this browser — said on Home, with whether it promised to keep it.
  const webStorage = useStore(webStorageStore, (s) => s);
  const profile = useInspectorProfile();
  const name = profile.name.trim();
  const now = Date.now();
  const inspections = state.inspections; // sorted by updatedAt desc (inspectionStore)
  const stages = useMemo(() => new Map(inspections.map((i) => [i.id, inspectionStageOf(i, state, findingState)] as const)), [inspections, state, findingState]);
  const stageOf = (inspection: V2Inspection): InspectionStage => stages.get(inspection.id) ?? 'inProgress';
  // A copy exported to another device (Owner 2026-10-20) is not open work here.
  const current = inspections.find((i) => isInspectionWorkable(i) && stageOf(i) !== 'reportComplete') ?? null;

  const today0 = dayStart(now);
  const tomorrow0 = dayStart(now, 1);
  const horizon = dayStart(now, 1 + UPCOMING_DAYS);
  const open = inspections.filter((i) => isInspectionWorkable(i));
  const byTime = (a: V2Inspection, b: V2Inspection) => a.inspectionAt - b.inspectionAt;
  const today = open.filter((i) => i.inspectionAt >= today0 && i.inspectionAt < tomorrow0).sort(byTime);
  const upcoming = open.filter((i) => i.inspectionAt >= tomorrow0 && i.inspectionAt < horizon).sort(byTime).slice(0, UPCOMING_LIMIT);

  const todos: Todo[] = [];
  for (const inspection of open) {
    const due = nyReportDue(inspection, areasOf(state, inspection.id), now);
    if (due) {
      todos.push({
        key: `due-${inspection.id}`,
        tone: due.overdue ? 'overdue' : 'due',
        title: streetOf(inspection, t('inspection.noAddress')),
        body: t(due.overdue ? 'home.reportOverdue' : 'home.reportDue', { date: formatDate(due.dueAt) }),
        open: () => navigate({ screen: 'report', inspectionId: inspection.id }),
        testId: due.overdue ? 'home-todo-overdue' : 'home-todo-due'
      });
    } else if (stageOf(inspection) === 'ready') {
      todos.push({
        key: `ready-${inspection.id}`,
        tone: 'ready',
        title: streetOf(inspection, t('inspection.noAddress')),
        body: t('home.todoReady'),
        open: () => navigate({ screen: 'closeout', inspectionId: inspection.id }),
        testId: 'home-todo-ready'
      });
    }
  }
  if (profile.licenseStatus !== 'licensed') {
    todos.push({
      key: 'profile',
      tone: 'profile',
      title: t('home.todoProfileTitle'),
      body: t('home.todoProfile'),
      open: () => navigate({ screen: 'settingsPage', page: 'profile' }),
      testId: 'home-todo-profile'
    });
  }

  const [month0, month1] = monthRange(now);
  const monthInspections = inspections.filter((i) => i.inspectionAt >= month0 && i.inspectionAt < month1);
  const monthReports = inspections.filter((i) => i.finalizedAt !== null && i.finalizedAt >= month0 && i.finalizedAt < month1).length;
  const monthFindings = monthInspections.reduce((sum, i) => sum + inspectionIssueCount(state, findingState, i.id), 0);

  const greetingKeys = GREETING_KEYS[dayPartOf(now)];
  const greeting = name.length > 0 ? t(greetingKeys.named, { name }) : t(greetingKeys.plain);
  // zh-CN "9月11日星期五" · en-US "Friday, September 11"
  const dateLine = new Date(now).toLocaleDateString(locale, { month: 'long', day: 'numeric', weekday: 'long' });
  const licensed = profile.licenseStatus === 'licensed' && profile.licensedState !== null;
  const newInspection = () => navigate({ screen: 'newInspection' });
  const openInspection = (inspection: V2Inspection) => navigate({ screen: 'inspection', inspectionId: inspection.id });

  // The plan row's second line. Owner 2026-09-11 「头三次免费」: while free reports remain they are what the
  // inspector actually has, so they are stated. Device walkthrough 2026-10-27 B4 (10-26 #3): with no plan the
  // row said only 「还有 3 份免费正式报告」 while 3 AI 单 (gift / 3-pack, server-counted) were left — those
  // are stated first now. With a plan the server's count already holds the period's 单 and the packs / gifts.
  function planRemainingLine(): string {
    if (billing.internalTester) return t('billing.internalTesterNote');
    if (billing.plan !== null) return tp('billing.remainingInspections', billing.remainingInspections);
    const aiUnits = billing.server !== null && billing.remainingInspections > 0 ? tp('home.planAiUnits', billing.remainingInspections) : null;
    const free = billing.trialRemaining > 0 ? tp('billing.trialRemaining', billing.trialRemaining) : billing.singleCredits > 0 || aiUnits === null ? tp('billing.singleCredits', billing.singleCredits) : null;
    return [aiUnits, free].filter((part): part is string => part !== null).join(' · ');
  }

  const actions: Array<{ key: string; icon: IconName; label: string; onClick: () => void }> = [
    { key: 'new', icon: 'add_home_work', label: t('inspections.new'), onClick: newInspection },
    { key: 'all', icon: 'apartment', label: t('home.allInspections'), onClick: () => switchTab('inspections') },
    { key: 'reports', icon: 'receipt_long', label: t('nav.reports'), onClick: () => switchTab('reports') },
    { key: 'profile', icon: 'badge', label: t('settings.profile'), onClick: () => navigate({ screen: 'settingsPage', page: 'profile' }) }
  ];

  return (
    <div className="screen dash" data-testid="home-screen">
      <header className="dash-top">
        {shell.tier === 'tablet' ? null : (
          <div className="dash-bar">
            <div className="dash-brand">
              <span className="dash-mark">
                <Icon name="verified_user" />
              </span>
              <span className="dash-app" data-testid="home-brand">
                {t('home.brand')}
              </span>
            </div>
            <div className="dash-bar-side">
              {shell.statusChip}
              <button type="button" className="avatar-hit" aria-label={t('shell.account')} onClick={shell.openAccount} data-testid="topbar-avatar">
                {shell.avatar ?? <span className="avatar">{name.length > 0 ? <span className="avatar-initials">{initialsOf(name)}</span> : <Icon name="person" />}</span>}
              </button>
            </div>
          </div>
        )}
        <h1 className="dash-greeting" data-testid="home-greeting">
          {greeting}
        </h1>
        <p className="dash-date">{dateLine}</p>
        {licensed ? (
          <span className="dash-license" data-testid="home-license">
            <Icon name="verified_user" />
            {t('home.license', { state: profile.licensedState ?? '', number: profile.licenseNumber })}
          </span>
        ) : (
          <button type="button" className="dash-license dash-license-missing" onClick={() => navigate({ screen: 'settingsPage', page: 'profile' })} data-testid="home-license-missing">
            <Icon name="badge" />
            {t('home.licenseMissing')}
          </button>
        )}
      </header>

      {isWebVersion() ? (
        <Banner
          tone={webStorage.persisted === false ? 'warning' : 'info'}
          icon="computer"
          title={t('home.webNoticeTitle')}
          body={webStorage.persisted === false ? `${t('home.webNoticeBody')} ${t('home.webNoticeNotKept')}` : t('home.webNoticeBody')}
          testId="home-web-notice"
        />
      ) : null}
      <OtherDevicesBanner />

      <section className="dash-focus" data-testid="home-focus">
        {current ? (
          <FocusInspection inspection={current} stage={stageOf(current)} onOpen={() => openInspection(current)} />
        ) : (
          <>
            <span className="dash-focus-icon">
              <Icon name="add_home_work" />
            </span>
            <h2 className="dash-focus-title">{t('home.focusEmptyTitle')}</h2>
            <p className="dash-focus-sub">{t('home.focusEmptyBody')}</p>
            <button type="button" className="dash-focus-cta" onClick={newInspection} data-testid="home-new">
              <Icon name="add" />
              {t('inspections.new')}
            </button>
          </>
        )}
      </section>

      {/* Owner-selected design 2026-09-14 (Figma 191:3 "HOME · A"): the quick actions under their own title. */}
      <section className="dash-section dash-section-actions">
        <div className="dash-section-head">
          <h2 className="dash-section-title">{t('home.quickActions')}</h2>
        </div>
        <nav className="dash-actions" aria-label={t('home.quickActions')} data-testid="home-actions">
          {actions.map((action) => (
            <button key={action.key} type="button" className="dash-action" onClick={action.onClick} data-testid={`home-action-${action.key}`}>
              <span className="dash-action-icon">
                <Icon name={action.icon} />
              </span>
              <span className="dash-action-label">{action.label}</span>
            </button>
          ))}
        </nav>
      </section>

      {aiJobs.length > 0 ? (
        <button type="button" className="dash-plan" onClick={() => navigate({ screen: 'aiTasks' })} data-testid="home-ai-tasks">
          <span className="dash-plan-icon">
            <Icon name="psychology" />
          </span>
          <span className="dash-row-main">
            <span className="dash-row-title">{t('aiTasks.title')}</span>
            <span className="dash-plan-meta" data-testid="home-ai-tasks-counts">
              {t('aiTasks.counts', { active: aiCounts.active, unread: aiCounts.unread })}
            </span>
          </span>
          <Icon name="chevron_right" className="dash-chevron" />
        </button>
      ) : null}

      {/* Plan and remaining inspections (Owner D-4, 2026-09-11: 显示大约剩几次). One row, only when
          purchases exist in this build; it opens the plan page where buying actually happens. */}
      {billing.storeConfigured ? (
        <button type="button" className="dash-plan" onClick={() => navigate({ screen: 'settingsPage', page: 'plan' })} data-testid="home-plan">
          <span className="dash-plan-icon">
            <Icon name="credit_card" />
          </span>
          <span className="dash-row-main">
            {/* Issue 20: an internal test account is never charged — its row says that, not 「没有套餐 · 0 份」. */}
            <span className="dash-row-title">{billing.internalTester ? t('billing.internalTester') : billing.plan === 'monthly' ? t('settings.planMonthly') : billing.plan === 'annual' ? t('settings.planAnnual') : t('billing.planNone')}</span>
            <span className="dash-plan-meta" data-testid="home-plan-remaining">
              {planRemainingLine()}
            </span>
          </span>
          <Icon name="chevron_right" className="dash-chevron" />
        </button>
      ) : null}

      <section className="dash-section" data-testid="home-schedule">
        <div className="dash-section-head">
          <h2 className="dash-section-title">{t('home.today')}</h2>
        </div>
        <div className="dash-card">
          {today.length > 0 ? (
            today.map((inspection) => (
              <ScheduleRow key={inspection.id} lead={formatClock(inspection.inspectionAt)} inspection={inspection} stage={stageOf(inspection)} onOpen={() => openInspection(inspection)} />
            ))
          ) : (
            <p className="dash-empty" data-testid="home-today-empty">
              {t('home.todayEmpty')}
            </p>
          )}
          {upcoming.length > 0 ? (
            <>
              <p className="dash-subhead">{t('home.upcoming')}</p>
              {/* Owner-selected design: the coming-up jobs as one line each — date · street · city. */}
              {upcoming.map((inspection) => (
                <UpcomingRow key={inspection.id} lead={new Date(inspection.inspectionAt).toLocaleDateString(locale, { month: 'numeric', day: 'numeric' })} inspection={inspection} onOpen={() => openInspection(inspection)} />
              ))}
            </>
          ) : null}
        </div>
        {today.length === 0 && upcoming.length === 0 ? <p className="dash-hint">{t('home.scheduleHint')}</p> : null}
      </section>

      <section className="dash-section" data-testid="home-todo">
        <div className="dash-section-head">
          <h2 className="dash-section-title">{t('home.todo')}</h2>
        </div>
        {/* Owner-selected design (Q4 = A): plain rows — the house on the first line, the reminder under it
            (the approved wording whole); no icon tiles, no count, no chevrons. */}
        {todos.length > 0 ? (
          <div className="dash-card">
            {todos.map((todo) => (
              <button key={todo.key} type="button" className={`dash-todo dash-todo-${todo.tone}`} onClick={todo.open} data-testid={todo.testId}>
                <span className="dash-row-main">
                  <span className="dash-row-title dash-todo-title">{todo.title}</span>
                  <span className="dash-todo-body">{todo.body}</span>
                </span>
              </button>
            ))}
          </div>
        ) : (
          <p className="dash-empty dash-empty-card" data-testid="home-todo-empty">
            {t('home.todoEmpty')}
          </p>
        )}
      </section>

      <section className="dash-section" data-testid="home-month">
        <div className="dash-section-head">
          <h2 className="dash-section-title">{t('home.month')}</h2>
          <span className="dash-section-meta">{new Date(now).toLocaleDateString(locale, { year: 'numeric', month: 'long' })}</span>
        </div>
        <div className="dash-stats">
          <Stat value={monthInspections.length} label={tp('home.statInspections', monthInspections.length)} testId="home-stat-inspections" />
          <Stat value={monthReports} label={tp('home.statReports', monthReports)} testId="home-stat-reports" />
          <Stat value={monthFindings} label={tp('home.statFindings', monthFindings)} testId="home-stat-findings" />
        </div>
      </section>

      <p className="dash-foot">
        <Icon name="cloud_off" />
        {t(isWebVersion() ? 'home.footer.web' : 'home.footer')}
      </p>
    </div>
  );
}

// The brand card: the inspection to continue — street · locality · item progress · areas and
// findings · last edited · Continue.
function FocusInspection(props: { inspection: V2Inspection; stage: InspectionStage; onOpen: () => void }) {
  const { t, tp } = useT();
  const state = useStore(inspectionStore, (s) => s);
  const findingState = useStore(findingStore, (s) => s);
  const inspection = props.inspection;
  const progress = inspectionProgress(state, inspection.id);
  // B2 (2026-10-27): the same 问题记录 number as the overview chip and its area cards.
  const issues = inspectionIssueCount(state, findingState, inspection.id);
  const completion = areaCompletionFor(state.areas.filter((a) => a.inspectionId === inspection.id));
  const pct = progress.total > 0 ? Math.round((progress.settled / progress.total) * 100) : 0;
  const locality = localityOf(inspection, t);
  const editedToday = new Date(inspection.updatedAt).toDateString() === new Date().toDateString();
  const meta = [
    tp('inspections.areasDone', completion.enabledAreas, { done: completion.completedAreas }),
    issues > 0 ? tp('common.findingsCount', issues) : null,
    t('inspections.lastEdited', { time: editedToday ? formatClock(inspection.updatedAt) : formatDate(inspection.updatedAt) })
  ].filter((part): part is string => part !== null);
  return (
    <>
      <div className="dash-focus-head">
        <p className="dash-focus-eyebrow">{t('home.focusEyebrow')}</p>
        <StageBadge stage={props.stage} />
      </div>
      <h2 className="dash-focus-title" data-testid="home-focus-title">
        {streetOf(inspection, t('inspection.noAddress'))}
      </h2>
      {locality.length > 0 ? <p className="dash-focus-sub">{locality}</p> : null}
      <div className="dash-focus-progress">
        <div className="dash-focus-progress-head">
          <span data-testid="home-focus-progress">{tp('common.itemsInspected', progress.total, { done: progress.settled })}</span>
          <strong>{t('common.percent', { n: pct })}</strong>
        </div>
        <div className="dash-focus-track" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div className="dash-focus-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <p className="dash-focus-meta" data-testid="home-focus-meta">
        {meta.join(' · ')}
      </p>
      <button type="button" className="dash-focus-cta" onClick={props.onOpen} data-testid="home-resume">
        {t('inspections.resume')}
        <Icon name="arrow_forward" />
      </button>
    </>
  );
}

function ScheduleRow(props: { lead: string; leadSub?: string; inspection: V2Inspection; stage: InspectionStage; onOpen: () => void }) {
  const { t } = useT();
  const city = props.inspection.property.city.trim();
  return (
    <button type="button" className="dash-row" onClick={props.onOpen} data-testid="home-schedule-row">
      <span className="dash-row-lead">
        <strong>{props.lead}</strong>
        {props.leadSub ? <span>{props.leadSub}</span> : null}
      </span>
      <span className="dash-row-main">
        <span className="dash-row-title">{streetOf(props.inspection, t('inspection.noAddress'))}</span>
        <span className="dash-row-sub">{city.length > 0 ? `${clientOf(props.inspection, t)} · ${city}` : clientOf(props.inspection, t)}</span>
      </span>
      <StageBadge stage={props.stage} />
    </button>
  );
}

function UpcomingRow(props: { lead: string; inspection: V2Inspection; onOpen: () => void }) {
  const { t } = useT();
  const city = props.inspection.property.city.trim();
  return (
    <button type="button" className="dash-row dash-row-compact" onClick={props.onOpen} data-testid="home-schedule-row">
      <span className="dash-row-line">{[props.lead, streetOf(props.inspection, t('inspection.noAddress')), city.length > 0 ? city : null].filter((part): part is string => part !== null).join(' · ')}</span>
    </button>
  );
}

function Stat(props: { value: number; label: string; testId: string }) {
  return (
    <div className="dash-stat" data-testid={props.testId}>
      <strong>{props.value}</strong>
      <span>{props.label}</span>
    </div>
  );
}
